import { useEffect, useMemo, useRef } from 'react'
import { toast } from 'sonner'
import { useNavPinsQuery } from '@/client/api/nav/nav-pins.query'
import { useSaveNavPinsMutation } from '@/client/api/nav/nav-pins.mutation'
import { useSidebarNavStore } from '@/client/stores/sidebar-nav'

/**
 * Hydrate pins from localStorage immediately, then reconcile with the server.
 * Local writes are optimistic; server PUTs are debounced.
 */
export function useNavPinsSync(enabled = true) {
  const hydrateFromLocal = useSidebarNavStore((s) => s.hydrateFromLocal)
  const setPinsFromServer = useSidebarNavStore((s) => s.setPinsFromServer)
  const pins = useSidebarNavStore((s) => s.pins)
  const hydrated = useSidebarNavStore((s) => s.hydrated)
  const setSyncing = useSidebarNavStore((s) => s.setSyncing)
  const setLastSyncError = useSidebarNavStore((s) => s.setLastSyncError)

  const saveMutation = useSaveNavPinsMutation()
  const query = useNavPinsQuery({ enabled })

  const skipNextPut = useRef(true)
  const dirty = useRef(false)
  const serverSettled = useRef(false)
  const debounceRef = useRef<number | null>(null)
  const lastPushed = useRef<string>('')

  useEffect(() => {
    hydrateFromLocal()
  }, [hydrateFromLocal])

  useEffect(() => {
    if (!enabled) return
    if (query.isSuccess && query.data != null) {
      serverSettled.current = true
      // Prefer in-flight local edits over a late GET response.
      if (dirty.current) {
        setLastSyncError(null)
        return
      }
      skipNextPut.current = true
      setPinsFromServer(query.data)
      lastPushed.current = JSON.stringify(query.data)
      setLastSyncError(null)
      return
    }
    if (query.isError) {
      serverSettled.current = true
    }
  }, [
    enabled,
    query.isSuccess,
    query.isError,
    query.data,
    setPinsFromServer,
    setLastSyncError,
  ])

  useEffect(() => {
    if (!enabled || !hydrated) return
    if (skipNextPut.current) {
      skipNextPut.current = false
      return
    }
    dirty.current = true
    const serialized = JSON.stringify(pins)
    if (serialized === lastPushed.current) return
    // Wait until the initial GET has settled so we don't race the server overwrite.
    if (!serverSettled.current && !query.isError && !query.isSuccess) return

    if (debounceRef.current) window.clearTimeout(debounceRef.current)
    debounceRef.current = window.setTimeout(() => {
      setSyncing(true)
      saveMutation.mutate(pins, {
        onSuccess: (saved) => {
          lastPushed.current = JSON.stringify(saved)
          dirty.current = false
          setLastSyncError(null)
          setSyncing(false)
        },
        onError: (err: any) => {
          setLastSyncError(err?.message ?? 'sync failed')
          setSyncing(false)
          toast.error('Saved locally; will sync when online')
        },
      })
    }, 300)

    return () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current)
    }
  }, [pins, enabled, hydrated, query.isSuccess, query.isError]) // eslint-disable-line react-hooks/exhaustive-deps

  return useMemo(
    () => ({
      pins,
      loading: !hydrated,
      syncing: saveMutation.isPending,
      serverLoading: query.isLoading,
    }),
    [pins, hydrated, saveMutation.isPending, query.isLoading],
  )
}
